import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Lock, Check, Watch } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, sessionId, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Payment, 3: Review

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
  });

  const total = getCartTotal();
  const shipping = total > 10000 ? 0 : 150;
  const finalTotal = total + shipping;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step < 3) {
      setStep(step + 1);
      return;
    }

    setIsProcessing(true);

    // Check if backend URL is configured
    if (!process.env.REACT_APP_BACKEND_URL) {
      setTimeout(() => {
        const mockOrder = {
          id: 'ord_' + Math.random().toString(36).substr(2, 9),
          total: finalTotal,
          items: cart.map(item => ({ ...item, name: item.product?.name })),
          customer_info: {
            email: formData.email
          }
        };
        toast.success('Order placed successfully!');
        navigate('/order-confirmation', { state: { order: mockOrder } });
        clearCart();
        setIsProcessing(false);
      }, 2000);
      return;
    }

    try {
      // Create order
      const orderData = {
        session_id: sessionId,
        items: cart.map((item) => ({
          product_id: item.product_id,
          name: item.product?.name,
          price: item.product?.price,
          quantity: item.quantity,
          size: item.size,
        })),
        total: finalTotal,
        customer_info: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          postalCode: formData.postalCode,
        },
      };

      const response = await axios.post(`${API}/orders`, orderData);

      // Simulate payment processing delay
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast.success('Order placed successfully!');
      navigate('/order-confirmation', { state: { order: response.data } });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center gap-6">
        <Watch className="w-16 h-16 text-white/10" />
        <p className="text-white/50 text-lg">Your cart is empty</p>
        <Link to="/">
          <Button className="bg-gold-500 text-black hover:bg-gold-400 rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold">
            Return to Shop
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="checkout-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/80 backdrop-blur-md">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Watch className="w-8 h-8 text-gold-500" />
              <span className="font-serif text-2xl text-white tracking-tight">CHRONOS</span>
            </Link>
            <div className="flex items-center gap-2 text-white/60">
              <Lock className="w-4 h-4" />
              <span className="text-sm">Secure Checkout</span>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-gold-500 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Shop</span>
          </Link>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 flex items-center justify-center font-serif text-lg ${step >= s ? 'bg-gold-500 text-black' : 'bg-white/5 text-white/30'
                    }`}
                >
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-16 h-px ${step > s ? 'bg-gold-500' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Customer Info */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-8"
                  >
                    <h2 className="font-serif text-2xl text-white">Customer Information</h2>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">First Name</Label>
                        <Input
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Last Name</Label>
                        <Input
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-last-name"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Phone</Label>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-phone"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Address</Label>
                      <Input
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="bg-white/5 border-white/10 text-white rounded-none h-12"
                        data-testid="checkout-address"
                      />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">City</Label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-city"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Country</Label>
                        <Input
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-country"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Postal Code</Label>
                        <Input
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-postal"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Payment */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-8"
                  >
                    <h2 className="font-serif text-2xl text-white">Payment Details</h2>
                    <p className="text-white/40 text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Demo Mode - No real payment will be processed
                    </p>

                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs uppercase tracking-wider">Card Number</Label>
                      <div className="relative">
                        <Input
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          placeholder="4242 4242 4242 4242"
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12 pr-12"
                          data-testid="checkout-card-number"
                        />
                        <CreditCard className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">Expiry Date</Label>
                        <Input
                          name="expiry"
                          value={formData.expiry}
                          onChange={handleInputChange}
                          placeholder="MM/YY"
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-expiry"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs uppercase tracking-wider">CVV</Label>
                        <Input
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleInputChange}
                          placeholder="123"
                          required
                          className="bg-white/5 border-white/10 text-white rounded-none h-12"
                          data-testid="checkout-cvv"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Review */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-8"
                  >
                    <h2 className="font-serif text-2xl text-white">Review Order</h2>

                    <div className="bg-white/5 p-6 border border-white/5 space-y-4">
                      <h3 className="text-gold-500 text-xs uppercase tracking-wider">Shipping To</h3>
                      <p className="text-white">
                        {formData.firstName} {formData.lastName}<br />
                        {formData.address}<br />
                        {formData.city}, {formData.postalCode}<br />
                        {formData.country}
                      </p>
                    </div>

                    <div className="bg-white/5 p-6 border border-white/5 space-y-4">
                      <h3 className="text-gold-500 text-xs uppercase tracking-wider">Payment Method</h3>
                      <p className="text-white flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Card ending in {formData.cardNumber.slice(-4) || '****'}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-4 mt-8">
                  {step > 1 && (
                    <Button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="bg-transparent border border-white/20 text-white hover:border-gold-500 hover:text-gold-500 rounded-none px-8 py-6 text-sm uppercase tracking-widest"
                      data-testid="checkout-back-btn"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={isProcessing}
                    className="flex-1 bg-gold-500 text-black hover:bg-gold-400 rounded-none py-6 text-sm uppercase tracking-widest font-bold btn-glow"
                    data-testid="checkout-continue-btn"
                  >
                    {isProcessing ? (
                      <span className="animate-pulse">Processing...</span>
                    ) : step === 3 ? (
                      `Place Order - $${finalTotal.toLocaleString()}`
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-charcoal border border-white/5 p-6 sticky top-24">
                <h3 className="font-serif text-xl text-white mb-6">Order Summary</h3>

                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-4" data-testid={`summary-item-${item.id}`}>
                      <div className="w-16 h-16 bg-obsidian">
                        <img
                          src={item.product?.image}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{item.product?.name}</p>
                        <p className="text-white/40 text-xs">Size: {item.size} × {item.quantity}</p>
                        <p className="text-gold-400 text-sm mt-1">
                          ${((item.product?.price || 0) * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="bg-white/5 my-6" />

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Subtotal</span>
                    <span className="text-white">${total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Shipping</span>
                    <span className="text-white">{shipping === 0 ? 'Complimentary' : `$${shipping}`}</span>
                  </div>
                </div>

                <Separator className="bg-white/5 my-6" />

                <div className="flex justify-between">
                  <span className="text-white uppercase tracking-wider text-sm">Total</span>
                  <span className="font-serif text-2xl text-gold-500" data-testid="checkout-total">
                    ${finalTotal.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
